import { useState, useEffect } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Bell, BellOff, Check, X } from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';
import Logo from '@/components/Logo';

const API_URL = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function PushNotificationsSettings() {
  const { user } = useAuth();
  const [isSupported, setIsSupported] = useState(false);
  const [isSubscribed, setIsSubscribed] = useState(false);
  const [loading, setLoading] = useState(false);
  const [permission, setPermission] = useState('default');

  useEffect(() => {
    checkPushSupport();
    checkSubscriptionStatus();
  }, []);

  const checkPushSupport = () => {
    const supported = 'serviceWorker' in navigator && 'PushManager' in window;
    setIsSupported(supported);
    
    if (supported && Notification.permission) {
      setPermission(Notification.permission);
    }
  };

  const checkSubscriptionStatus = async () => {
    if (!('serviceWorker' in navigator)) return;

    try {
      const registration = await navigator.serviceWorker.ready;
      const subscription = await registration.pushManager.getSubscription();
      setIsSubscribed(!!subscription);
    } catch (error) {
      console.error('Error checking subscription:', error);
    }
  };

  const urlBase64ToUint8Array = (base64String) => {
    const padding = '='.repeat((4 - (base64String.length % 4)) % 4);
    const base64 = (base64String + padding).replace(/\\-/g, '+').replace(/_/g, '/');
    const rawData = window.atob(base64);
    const outputArray = new Uint8Array(rawData.length);
    for (let i = 0; i < rawData.length; ++i) {
      outputArray[i] = rawData.charCodeAt(i);
    }
    return outputArray;
  };

  const subscribeToPush = async () => {
    if (!isSupported) {
      toast.error('Push notifications non supportate dal browser');
      return;
    }

    setLoading(true);

    try {
      // Request notification permission
      const permissionResult = await Notification.requestPermission();
      setPermission(permissionResult);

      if (permissionResult !== 'granted') {
        toast.error('Permesso negato per le notifiche');
        setLoading(false);
        return;
      }

      // Get VAPID public key from backend
      const keyResponse = await axios.get(`${API_URL}/push/vapid-public-key`);
      const publicKey = keyResponse.data.publicKey;

      // Register service worker and subscribe
      const registration = await navigator.serviceWorker.ready;
      
      const subscription = await registration.pushManager.subscribe({
        userVisibleOnly: true,
        applicationServerKey: urlBase64ToUint8Array(publicKey)
      });

      // Send subscription to backend
      await axios.post(`${API_URL}/push/subscribe`, subscription.toJSON());

      setIsSubscribed(true);
      toast.success('Notifiche push attivate!');
    } catch (error) {
      console.error('Error subscribing to push:', error);
      toast.error("Errore nell'attivazione delle notifiche");
    } finally {
      setLoading(false);
    }
  };

  const unsubscribeFromPush = async () => {
    setLoading(true);

    try {
      const registration = await navigator.serviceWorker.ready;
      const subscription = await registration.pushManager.getSubscription();

      if (subscription) {
        await subscription.unsubscribe();
        await axios.delete(`${API_URL}/push/unsubscribe?endpoint=${encodeURIComponent(subscription.endpoint)}`);
      }

      setIsSubscribed(false);
      toast.success('Notifiche push disattivate');
    } catch (error) {
      console.error('Error unsubscribing:', error);
      toast.error('Errore nella disattivazione delle notifiche');
    } finally {
      setLoading(false);
    }
  };

  const sendTestNotification = async () => {
    try {
      await axios.post(`${API_URL}/push/test`);
      toast.success('Notifica di test inviata!');
    } catch (error) {
      toast.error("Errore nell'invio della notifica di test");
    }
  };

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-white border-b border-gray-200">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <div className="flex items-center gap-4">
            <Logo size="md" linkTo="/dashboard" />
            <div>
              <h1 className="text-2xl font-bold text-gray-900">Push Notifications</h1>
              <p className="text-sm text-gray-600">Gestisci le notifiche push per il tuo account</p>
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {!isSupported ? (
          <Card>
            <CardContent className="py-8 text-center">
              <BellOff className="w-12 h-12 text-gray-400 mx-auto mb-4" />
              <h3 className="text-lg font-semibold mb-2">Browser Non Supportato</h3>
              <p className="text-gray-600">
                Il tuo browser non supporta le notifiche push. 
                Prova con Chrome, Firefox, Edge o Safari più recenti.
              </p>
            </CardContent>
          </Card>
        ) : (
          <div className="space-y-6">
            {/* Status Card */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Bell className="w-5 h-5" />
                  Stato Notifiche
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-4">
                  <div className="flex items-center justify-between p-4 bg-gray-50 rounded-lg">
                    <div>
                      <p className="font-medium">Stato Subscription</p>
                      <p className="text-sm text-gray-600">
                        {isSubscribed ? 'Attivo' : 'Non attivo'}
                      </p>
                    </div>
                    <div>
                      {isSubscribed ? (
                        <Check className="w-6 h-6 text-green-600" />
                      ) : (
                        <X className="w-6 h-6 text-gray-400" />
                      )}
                    </div>
                  </div>

                  <div className="flex items-center justify-between p-4 bg-gray-50 rounded-lg">
                    <div>
                      <p className="font-medium">Permesso Browser</p>
                      <p className="text-sm text-gray-600 capitalize">{permission}</p>
                    </div>
                    <div>
                      {permission === 'granted' ? (
                        <Check className="w-6 h-6 text-green-600" />
                      ) : permission === 'denied' ? (
                        <X className="w-6 h-6 text-red-600" />
                      ) : (
                        <X className="w-6 h-6 text-gray-400" />
                      )}
                    </div>
                  </div>
                </div>

                <div className="mt-6 space-y-3">
                  {!isSubscribed ? (
                    <Button
                      onClick={subscribeToPush}
                      disabled={loading}
                      className="w-full"
                    >
                      <Bell className="w-4 h-4 mr-2" />
                      {loading ? 'Attivazione...' : 'Attiva Notifiche Push'}
                    </Button>
                  ) : (
                    <>
                      <Button
                        onClick={sendTestNotification}
                        variant="outline"
                        className="w-full"
                      >
                        <Bell className="w-4 h-4 mr-2" />
                        Invia Notifica di Test
                      </Button>
                      <Button
                        onClick={unsubscribeFromPush}
                        disabled={loading}
                        variant="destructive"
                        className="w-full"
                      >
                        <BellOff className="w-4 h-4 mr-2" />
                        {loading ? 'Disattivazione...' : 'Disattiva Notifiche'}
                      </Button>
                    </>
                  )}
                </div>
              </CardContent>
            </Card>

            {/* Info Card */}
            <Card>
              <CardHeader>
                <CardTitle>Cosa sono le Push Notifications?</CardTitle>
              </CardHeader>
              <CardContent className="space-y-3 text-sm text-gray-600">
                <p>
                  Le notifiche push ti permettono di ricevere aggiornamenti importanti anche quando non hai la piattaforma aperta:
                </p>
                <ul className="list-disc list-inside space-y-2 ml-4">
                  <li>Promemoria per eventi imminenti</li>
                  <li>Notifiche di nuovi check-in</li>
                  <li>Aggiornamenti sui tuoi eventi</li>
                  <li>Messaggi importanti dalla piattaforma</li>
                </ul>
                <p className="text-xs text-gray-500 mt-4">
                  Le notifiche rispettano la tua privacy e puoi disattivarle in qualsiasi momento.
                </p>
              </CardContent>
            </Card>
          </div>
        )}
      </div>
    </div>
  );
}
